import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { useThemeColours } from '@/shared/lib/theme/use-theme-colours';
import { useLocale } from '@/shared/lib/i18n/locale-context';

/**
 * A quantity per category.
 *
 * The Apex configuration is TailAdmin's (MIT — `shared/tailadmin/LICENSE.md`), stripped of the
 * twelve months of invented sales it shipped with and of its hard-coded indigo. See
 * [AreaChart](./AreaChart.tsx) for why the colours are read rather than written.
 */
const TOKENS = ['primary', 'secondary', 'info'] as const;

export function BarChart({
  series,
  categories,
  height = 220,
  horizontal = false,
  formatValue,
}: {
  series: readonly { name: string; data: readonly number[] }[];
  categories: readonly string[];
  height?: number;

  /** ⚠️ Turn this on when the category labels are names rather than dates — an Arabic region name
   *  rotated 90° under a vertical bar is a label nobody reads twice. */
  horizontal?: boolean;

  formatValue?: (value: number) => string;
}) {
  const colours = useThemeColours(TOKENS);
  const { locale } = useLocale();

  const options: ApexOptions = {
    colors: [colours.primary, colours.secondary, colours.info],
    chart: {
      fontFamily: 'inherit',
      type: 'bar',
      height,
      toolbar: { show: false },
      animations: { enabled: false },
    },
    plotOptions: {
      bar: { horizontal, columnWidth: '39%', borderRadius: 5, borderRadiusApplication: 'end' },
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 4, colors: ['transparent'] },
    legend: { show: series.length > 1, position: 'top', horizontalAlign: 'left' },
    grid: { yaxis: { lines: { show: true } } },
    fill: { opacity: 1 },
    tooltip: {
      x: { show: false },
      y: { formatter: formatValue ?? ((value: number) => String(value)) },
    },
    xaxis: {
      categories: [...categories],
      axisBorder: { show: false },
      axisTicks: { show: false },

      // ⚠️ Turning the bars on their side swaps which axis carries the NUMBERS: horizontally it is
      // this one, vertically it is `yaxis` below. Formatting both unconditionally would run the
      // category names — «عُثر على المفقود سالماً» — through a number formatter.
      //
      // ⚠️ Apex types the x-axis formatter as taking a STRING even when the axis is numeric — the
      // signature follows the axis's declared type, not the values it actually receives. The cast is
      // at the boundary and the coercion is explicit, rather than typing `formatValue` loosely and
      // losing the check everywhere else it is used.
      ...(horizontal && formatValue
        ? { labels: { formatter: (value: string) => formatValue(Number(value)) } }
        : {}),
    },
    /*
     * ⚠️ **The axis is formatted too, not only the tooltip.**
     *
     * Apex numbers its own axis with Latin digits whatever the page's locale is, and this panel
     * writes its figures in Arabic-Indic ones. Left alone, a single card carries ٦ in its list and 6
     * on the scale beside it — the same number in two alphabets, which reads as two numbers.
     * Observed on the statistics screen against live data before it was fixed.
     */
    yaxis: {
      title: { text: undefined },
      ...(!horizontal && formatValue ? { labels: { formatter: formatValue } } : {}),
    },
  };

  return (
    <div className="custom-scrollbar max-w-full overflow-x-auto" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className="min-w-[560px] xl:min-w-full">
        <Chart options={options} series={series as ApexOptions['series']} type="bar" height={height} />
      </div>
    </div>
  );
}
